<template>
  <div class="pa-comm-est">
    <div class="head">
      <div class="lbl">
        {{ t('property.shell.commissionLockedCard.title') }}
        <span class="pa-est-pill">{{ t('property.access.commissionEstimateCard.estimateBadge') }}</span>
      </div>
      <span class="tier-pill">
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M12 2l3 7 7 .5-5.5 4.5L18 21l-6-3.8L6 21l1.5-7L2 9.5 9 9z" />
        </svg>
        {{ tierKey }} · {{ tierPercent }}%
      </span>
    </div>

    <div class="pa-est-body">
      <div class="pa-est-hint">
        {{ t('property.access.commissionEstimateCard.basedOnSample', { unit: sampleUnitLabel }) }}
      </div>
      <div class="pa-est-row">
        <span class="k">{{ t('property.access.commissionEstimateCard.brokerageFee') }}</span>
        <span class="v">~ {{ formatNumber(sampleBrokerageAed) }}</span>
      </div>
      <div class="pa-est-row">
        <span class="k">{{ t('property.access.commissionEstimateCard.yourShare', { percent: tierPercent }) }}</span>
        <span class="v">~ {{ formatNumber(sampleShareAed) }}</span>
      </div>
      <div class="pa-est-est">
        <div class="k">{{ t('property.access.commissionEstimateCard.youEarnEstimate') }}</div>
        <div class="v">
          <span class="cur">AED</span>
          ~ {{ formatNumber(sampleShareAed) }}
        </div>
      </div>
    </div>

    <div class="pa-est-foot">
      <span class="ic">
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect
            x="4"
            y="11"
            width="16"
            height="10"
            rx="2"
          />
          <path d="M8 11V8a4 4 0 1 1 8 0v3" />
        </svg>
      </span>
      {{ t('property.access.commissionEstimateCard.exactCommissionNote', { developer: developerName }) }}
    </div>

    <button
      type="button"
      class="pa-est-cta"
      @click="emit('verify-click')"
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M12 3l8 3v6c0 5-3.4 8.5-8 9.5C7.4 20.5 4 17 4 12V6l8-3z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
      <span
        >{{ t('property.access.commissionEstimateCard.verifyArrow') }}
        <span
          class="inline-block rtl:-scale-x-100"
          aria-hidden="true"
          >→</span
        ></span
      >
    </button>
  </div>
</template>

<script setup lang="ts">
const { t } = useI18n();
const { formatNumber: formatNumberBase } = useFormat();

defineProps<{
  developerName: string;
  tierKey: string;
  tierPercent: number;
  sampleUnitLabel: string;
  sampleBrokerageAed: number;
  sampleShareAed: number;
}>();

const emit = defineEmits<{
  (e: 'verify-click'): void;
}>();

const formatNumber = (n: number) => {
  if (!Number.isFinite(n)) return '—';
  return formatNumberBase(n, { maximumFractionDigits: 0 });
};
</script>

<style scoped lang="scss">
.pa-comm-est {
  background: #e6f4ec;
  border-radius: 16px;
  padding: 22px 18px;
  border: 1px solid rgba(34, 197, 94, 0.3);

  .head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;

    .lbl {
      font-size: 13px;
      font-weight: 700;
      color: #0d0f15;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .tier-pill {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 3px 8px;
      border-radius: 6px;
      background: linear-gradient(135deg, #fff8ed, #fff0da);
      border: 1px solid rgba(255, 182, 72, 0.4);
      font-family: 'JetBrains Mono', ui-monospace, monospace;
      font-size: 9px;
      font-weight: 700;
      color: #b8821a;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }
  }

  .pa-est-pill {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    padding: 3px 7px;
    border-radius: 5px;
    background: rgba(13, 15, 21, 0.08);
    color: rgba(13, 15, 21, 0.7);
  }

  .pa-est-body {
    padding: 4px 0 12px;
  }

  .pa-est-hint {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #126b2f;
    margin-bottom: 10px;
  }

  .pa-est-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-size: 12.5px;
    padding: 4px 0;

    .k {
      color: #126b2f;
    }

    .v {
      font-weight: 700;
      color: #0d0f15;
      font-variant-numeric: tabular-nums;
    }
  }

  .pa-est-est {
    margin-top: 10px;
    padding-top: 12px;
    border-top: 1px solid rgba(18, 107, 47, 0.22);
    display: flex;
    justify-content: space-between;
    align-items: baseline;

    .k {
      font-size: 12px;
      color: #126b2f;
    }

    .v {
      font-family: 'Fraunces', 'Times New Roman', serif;
      font-weight: 600;
      font-size: 24px;
      letter-spacing: -0.02em;
      color: #0d0f15;
      font-variant-numeric: tabular-nums;

      .cur {
        font-family: 'JetBrains Mono', ui-monospace, monospace;
        font-size: 11px;
        color: #126b2f;
        margin-inline-end: 4px;
        font-weight: 500;
      }
    }
  }

  .pa-est-foot {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid rgba(18, 107, 47, 0.22);
    font-size: 12px;
    color: #126b2f;
    line-height: 1.5;
    display: flex;
    gap: 8px;
    align-items: flex-start;

    .ic {
      width: 22px;
      height: 22px;
      border-radius: 6px;
      background: white;
      color: #126b2f;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      margin-top: -1px;
    }
  }

  .pa-est-cta {
    margin-top: 14px;
    width: 100%;
    height: 44px;
    padding: 0 16px;
    border-radius: 11px;
    background: #0d0f15;
    color: white;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 700;
    border: none;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: #25272c;
    }
  }
}
</style>
